import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext.tsx'
import { api } from '../api.ts'
import { money, serviceStatusLabel, serviceTypeLabel } from '../format.ts'
import { NewTicketDialog } from '../components/NewTicketDialog.tsx'

interface Ticket {
  id: string
  number: string
  customerName: string
  serialNumber: string | null
  model: string | null
  machineDescription: string | null
  serviceType: string
  status: string
  claimStatus: string
  reportedAt: string
  warrantyActive: boolean
  totalNet: string | null
}

const STATUS_STYLE: Record<string, string> = {
  przyjete: 'bg-slate-100 text-slate-600',
  w_trakcie: 'bg-blue-100 text-blue-700',
  oczekiwanie_na_czesci: 'bg-amber-100 text-amber-700',
  zakonczone: 'bg-green-100 text-green-700',
}

export function ServicePage() {
  const { user } = useAuth()
  // Tickets are written by service and the owner (grants 0015); sales reads.
  const mozeZglaszac = user?.role === 'wlasciciel' || user?.role === 'serwis'
  const nav = useNavigate()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [search, setSearch] = useState('')
  const [onlyOpen, setOnlyOpen] = useState(true)
  const [dialog, setDialog] = useState(false)

  function reload() {
    const p = new URLSearchParams()
    if (search.trim()) p.set('search', search.trim())
    if (onlyOpen) p.set('open', 'true')
    api.get<{ tickets: Ticket[] }>(`/api/service-tickets?${p.toString()}`).then((r) => setTickets(r.tickets)).catch(() => {})
  }
  useEffect(() => {
    const t = setTimeout(reload, 120)
    return () => clearTimeout(t)
  }, [search, onlyOpen])

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Serwis</h1>
          <p className="text-sm text-slate-500">Zgłoszenia gwarancyjne i pogwarancyjne, przeglądy.</p>
        </div>
        {mozeZglaszac && (
          <button onClick={() => setDialog(true)} className="text-sm bg-brand-600 text-white px-4 py-2 rounded-md hover:bg-brand-700 font-medium shrink-0">
            Nowe zgłoszenie
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Numer zgłoszenia, seryjny, klient…" className="flex-1 min-w-56 px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:border-brand-600" />
        <label className="flex items-center gap-1.5 text-sm text-slate-600 px-2">
          <input type="checkbox" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />
          Tylko otwarte
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-400">
            <tr>
              <th className="text-left font-medium px-4 py-2">Numer</th>
              <th className="text-left font-medium px-4 py-2">Klient / maszyna</th>
              <th className="text-left font-medium px-4 py-2">Rodzaj</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-right font-medium px-4 py-2">Koszt</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-2">
                  <Link to={`/serwis/${t.id}`} className="text-brand-700 font-medium hover:underline">
                    {t.number}
                  </Link>
                </td>
                <td className="px-4 py-2 text-slate-700">
                  {t.customerName}
                  <span className="block text-xs text-slate-400">
                    {t.model ?? t.machineDescription}
                    {t.serialNumber && ` · ${t.serialNumber}`}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className="text-slate-600">{serviceTypeLabel(t.serviceType)}</span>
                  {t.warrantyActive && <span className="block text-xs text-green-600">w gwarancji</span>}
                </td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLE[t.status] ?? ''}`}>{serviceStatusLabel(t.status)}</span>
                </td>
                <td className="px-4 py-2 text-right tabular text-slate-600">{money(t.totalNet)}</td>
              </tr>
            ))}
            {tickets.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">Brak zgłoszeń.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {dialog && <NewTicketDialog onClose={() => setDialog(false)} onCreated={(id) => nav(`/serwis/${id}`)} />}
    </div>
  )
}
